import clsx from "clsx"
import axios from "axios"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { CheckIcon, SparklesIcon } from "@heroicons/react/20/solid"
import useAuth from "../../hooks/useAuth"
import { Container } from "../landing/components/Container"
import { FullscreenLoader } from "../ui/Loader"
import { Button } from "../ui/button"
import {
  SubscriptionPlanType,
  formatPlanPeriod,
  formatPlanPrice,
  getPlanByType
} from "../../api/domainModels/subscriptionPlan"
import { useCreateStripeSession } from "../../api/queries/billing"
import { errorToast } from "../ui/Toast"

function PriceToggle({
  isYearly,
  setIsYearly
}: {
  isYearly: boolean
  setIsYearly: (value: boolean) => void
}) {
  return (
    <div className="flex items-center justify-center gap-3">
      <span className={`text-sm ${!isYearly ? "text-white" : "text-gray-500"}`}>Monthly</span>
      <button
        type="button"
        aria-label="Toggle yearly billing"
        onClick={() => setIsYearly(!isYearly)}
        className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
      >
        <span
          className={clsx(
            "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-emerald-500 transition-transform",
            isYearly ? "translate-x-5" : "translate-x-0"
          )}
        />
      </button>
      <span className={`text-sm ${isYearly ? "text-white" : "text-gray-500"}`}>
        Yearly
      </span>
      {isYearly && (
        <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-500/20">
          Save 17%
        </span>
      )}
    </div>
  )
}

function PlanCard({
  plan,
  price,
  period,
  features,
  onClick,
  ctaLabel,
  loading,
  featured
}: {
  plan: string
  price: string
  period?: string
  features: string[]
  onClick: () => void
  ctaLabel: string
  loading: boolean
  featured?: boolean
}) {
  return (
    <div
      className={clsx(
        "relative flex flex-col rounded-2xl border p-6 transition-all",
        featured
          ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-gray-900 to-gray-950 shadow-[0_0_32px_-12px_rgba(16,185,129,0.35)]"
          : "border-gray-800 bg-gray-900/60"
      )}
    >
      {featured && (
        <span className="absolute -top-3 right-5 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-0.5 text-xs font-semibold text-gray-950 shadow">
          <SparklesIcon className="h-3 w-3" aria-hidden />
          Recommended
        </span>
      )}
      <div>
        <h3 className={clsx("text-sm font-semibold uppercase tracking-widest", featured ? "text-emerald-400" : "text-gray-400")}>
          {plan}
        </h3>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-4xl font-bold tracking-tight text-white">{price}</span>
          {period && <span className="text-sm text-gray-500">{period}</span>}
        </div>
      </div>
      <ul className="mt-6 space-y-2.5">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-gray-200">
            <CheckIcon
              aria-hidden
              className={clsx(
                "mt-0.5 h-4 w-4 shrink-0",
                featured ? "text-emerald-400" : "text-gray-400"
              )}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 pt-2">
        <Button
          type="button"
          onClick={onClick}
          loading={loading}
          variant={featured ? "solidEmerald" : "solidWhite"}
          className="w-full"
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  )
}

function checkoutErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { errors?: { message?: string }[] } | undefined
    const msg = data?.errors?.[0]?.message
    if (msg) return msg
    if (err.response?.status === 401) return "Please sign in again to subscribe."
  }
  if (err instanceof Error && err.message) return err.message
  return "Could not start checkout. Please try again."
}

export function Pricing({
  variant = "page",
  onCheckoutStarted
}: {
  variant?: "page" | "modal"
  onCheckoutStarted?: () => void
}) {
  const router = useRouter()
  const { user, loading: authLoading } = useAuth()
  const [loading, setLoading] = useState(false)
  const [isYearly, setIsYearly] = useState(false)
  const { mutateAsync } = useCreateStripeSession()

  const freePlan = getPlanByType(SubscriptionPlanType.FREE)
  const proPlan = getPlanByType(SubscriptionPlanType.STARTER)

  const startCheckout = async () => {
    if (authLoading) {
      errorToast("Still loading your session. Try again in a moment.")
      return
    }
    if (!user?.email) {
      onCheckoutStarted?.()
      router.push("/auth/sign-up")
      return
    }
    setLoading(true)
    try {
      const payload = await mutateAsync({
        email: user.email,
        isYearly,
        planType: SubscriptionPlanType.STARTER
      })
      onCheckoutStarted?.()
      if (payload.url) {
        window.location.href = payload.url
      } else if (payload.subscriptionId) {
        window.location.reload()
      } else {
        errorToast("Checkout did not return a URL. Please try again.")
      }
    } catch (err) {
      errorToast(checkoutErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const pickFree = () => {
    onCheckoutStarted?.()
    if (!user) {
      router.push("/auth/sign-up")
    } else {
      router.push("/dashboard")
    }
  }

  const inner = (
    <>
      <FullscreenLoader show={loading} />
      <div className={variant === "page" ? "text-center" : "text-center"}>
        <h2
          className={clsx(
            "font-display tracking-tight text-white",
            variant === "page" ? "text-3xl sm:text-4xl" : "text-xl sm:text-2xl"
          )}
        >
          Simple pricing, no surprises
        </h2>
        <p className={clsx("text-gray-400", variant === "page" ? "mt-3 text-base" : "mt-2 text-sm")}>
          Start free. Upgrade when your tests outgrow the limits.
        </p>
      </div>

      <div className={clsx(variant === "page" ? "mt-8" : "mt-5")}>
        <PriceToggle isYearly={isYearly} setIsYearly={setIsYearly} />
      </div>

      <div
        className={clsx(
          "mx-auto grid gap-4 sm:grid-cols-2",
          variant === "page" ? "mt-10 max-w-3xl" : "mt-6 max-w-2xl"
        )}
      >
        <PlanCard
          plan={freePlan.name}
          price={formatPlanPrice(SubscriptionPlanType.FREE, isYearly)}
          period={formatPlanPeriod(isYearly)}
          features={freePlan.features}
          onClick={pickFree}
          ctaLabel={user ? "Current plan" : freePlan.ctaLabel}
          loading={false}
        />
        <PlanCard
          plan={proPlan.name}
          price={formatPlanPrice(SubscriptionPlanType.STARTER, isYearly)}
          period={formatPlanPeriod(isYearly)}
          features={proPlan.features}
          onClick={startCheckout}
          ctaLabel={proPlan.ctaLabel}
          loading={loading || authLoading}
          featured
        />
      </div>
    </>
  )

  if (variant === "modal") {
    return <div className="bg-gray-950 px-3 pb-4 pt-2">{inner}</div>
  }

  return (
    <section id="pricing" aria-label="Pricing" className="bg-gray-950 py-20 sm:py-32">
      <Container>{inner}</Container>
    </section>
  )
}
